import { z } from 'zod';
const start = (key, label) => ({ key, label, unlock: 'start' });
const locked = (key, label, unlock) => ({
    key,
    label,
    unlock,
});
export const AVATAR_OPTIONS = {
    background: [
        start('mint', 'Mint'),
        start('sunrise', 'Sunrise'),
        start('lagoon', 'Lagoon'),
        start('lilac', 'Lilac'),
        start('sand', 'Sand'),
        locked('reef', 'Reef', 'aussie-animals'),
    ],
    skin: [
        start('sand', 'Sand'),
        start('honey', 'Honey'),
        start('clay', 'Clay'),
        start('umber', 'Umber'),
        start('cocoa', 'Cocoa'),
        start('ebony', 'Ebony'),
    ],
    hair: [
        start('short', 'Short'),
        start('curls', 'Curls'),
        start('bunches', 'Bunches'),
        start('ponytail', 'Ponytail'),
        start('wavy', 'Wavy'),
        start('buzz', 'Buzz'),
        start('braids', 'Braids'),
        locked('spikes', 'Spikes', 'dino-squad'),
    ],
    hairColour: [
        start('ink', 'Ink'),
        start('chestnut', 'Chestnut'),
        start('sand', 'Sand'),
        start('ginger', 'Ginger'),
        start('silver', 'Silver'),
        locked('berry', 'Berry', 'star-poppers'),
        locked('ocean', 'Ocean', 'star-poppers'),
    ],
    eyes: [
        start('round', 'Round'),
        start('happy', 'Happy'),
        start('wide', 'Wide'),
        start('wink', 'Wink'),
        start('sparkle', 'Sparkle'),
    ],
    mouth: [
        start('smile', 'Smile'),
        start('grin', 'Grin'),
        start('giggle', 'Giggle'),
        start('calm', 'Calm'),
    ],
    outfit: [
        start('tee', 'Tee'),
        start('stripes', 'Stripes'),
        start('hoodie', 'Hoodie'),
        start('dress', 'Dress'),
        locked('jersey', 'Footy jersey', 'footy-legends'),
        locked('spacesuit', 'Spacesuit', 'star-poppers'),
    ],
    accessory: [
        start('none', 'None'),
        start('cap', 'Cap'),
        start('headband', 'Headband'),
        start('glasses', 'Glasses'),
        start('flower', 'Flower'),
        locked('snorkel', 'Snorkel', 'aussie-animals'),
    ],
};
export const AVATAR_PARTS = Object.keys(AVATAR_OPTIONS);
/** Human labels for the builder's tabs, in the order a child meets them. */
export const AVATAR_PART_LABELS = {
    skin: 'Skin',
    hair: 'Hair',
    hairColour: 'Hair colour',
    eyes: 'Eyes',
    mouth: 'Smile',
    outfit: 'Clothes',
    accessory: 'Extras',
    background: 'Background',
};
export const AVATAR_PART_ORDER = [
    'skin',
    'hair',
    'hairColour',
    'eyes',
    'mouth',
    'outfit',
    'accessory',
    'background',
];
function keysOf(part) {
    const keys = AVATAR_OPTIONS[part].map((o) => o.key);
    return keys;
}
/**
 * Strict: an unknown part or an unknown option key is rejected rather than
 * quietly dropped, so a stored avatar always renders to something real.
 */
export const avatarConfigSchema = z
    .object({
    skin: z.enum(keysOf('skin')),
    hair: z.enum(keysOf('hair')),
    hairColour: z.enum(keysOf('hairColour')),
    eyes: z.enum(keysOf('eyes')),
    mouth: z.enum(keysOf('mouth')),
    outfit: z.enum(keysOf('outfit')),
    accessory: z.enum(keysOf('accessory')),
    background: z.enum(keysOf('background')),
})
    .strict();
export const DEFAULT_AVATAR = {
    skin: 'honey',
    hair: 'short',
    hairColour: 'chestnut',
    eyes: 'happy',
    mouth: 'smile',
    outfit: 'tee',
    accessory: 'none',
    background: 'mint',
};
export function findAvatarOption(part, key) {
    return AVATAR_OPTIONS[part].find((o) => o.key === key);
}
/**
 * Every option in a config that the child has not unlocked. Empty means the
 * whole config is allowed. Returned as a list so the error can name them.
 */
export function lockedSelections(config, unlockedSets) {
    const owned = new Set(unlockedSets);
    const problems = [];
    for (const part of AVATAR_PARTS) {
        const option = findAvatarOption(part, config[part]);
        if (!option) {
            problems.push(`${part}:${config[part]}`);
            continue;
        }
        if (option.unlock !== 'start' && !owned.has(option.unlock)) {
            problems.push(`${part}:${option.key}`);
        }
    }
    return problems;
}
/** Falls back to the default for anything unreadable, so a corrupted or
 *  older stored config still renders a face instead of crashing a child's
 *  screen. Read path only - the write path uses the strict schema. */
export function coerceAvatarConfig(value) {
    const parsed = avatarConfigSchema.safeParse(value);
    if (parsed.success)
        return parsed.data;
    const source = (value ?? {});
    const out = { ...DEFAULT_AVATAR };
    for (const part of AVATAR_PARTS) {
        const candidate = source[part];
        if (typeof candidate === 'string' && findAvatarOption(part, candidate)) {
            out[part] = candidate;
        }
    }
    return out;
}
//# sourceMappingURL=avatar.js.map